import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  type CanvasHeroImageFields,
  getCanvasHeroImageUrl,
} from "@cohesive/api-client";
import type {
  CanvasEntity,
  Canvas as CanvasLink,
  Entity,
} from "@cohesive/api-entities";
import { ExternalLink, Frame } from "lucide-react";
import { useEffect, useState } from "react";
import { useThemeContext } from "../../../context/ThemeContext";
import {
  BaseEntityCard,
  BaseEntityCardIcon,
  type BaseEntityCardProps,
} from "../BaseCard";

export type { CanvasLink };

/** `{ type: "canvas" }` — a canvas placed on another canvas as a linked card. */
export function isCanvasLinkEntity(entity: Entity): entity is CanvasEntity {
  return "type" in entity && entity.type === "canvas";
}

/**
 * Live state the host resolves for a placed canvas link. The entity itself
 * carries only id + name, so hero and location come from the canvas record
 * and stay fresh across renames and moves.
 */
export interface CanvasLinkCardHydration {
  hero?: CanvasHeroImageFields;
  /** Workspace the canvas lives in ("Marketing", "My workspace", …). */
  locationLabel?: string;
  /** The canvas no longer resolves for this viewer (trashed or access lost). */
  isGone?: boolean;
}

export type CanvasLinkCardProps = CanvasLink &
  CanvasLinkCardHydration &
  BaseEntityCardProps & {
    selected?: boolean;
    /** Host (React Flow node) owns the chrome — drop ours and fill its box. */
    embedded?: boolean;
    /** Footer "open" action, right of the title (hover-revealed like the
     *  workspace card kebab). The host decides what opening means — on the
     *  canvas it is a new workspace tab. Omit to hide the button. */
    onOpenClick?: () => void;
  };

export function CanvasLinkCardAvatar({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Frame className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function CanvasLinkCard({
  id: _id,
  name,
  workspace_id: _workspaceId,
  hero,
  locationLabel,
  isGone,
  selected = false,
  embedded = false,
  onOpenClick,
  onClick,
  onDoubleClick,
  onQuickChatSubmit,
  actions,
  processes,
  toolbarMode,
  chatOpen,
  onChatOpenChange,
  className,
  style,
}: CanvasLinkCardProps) {
  const [imageError, setImageError] = useState(false);
  const { mode } = useThemeContext();
  const heroImageUrl = getCanvasHeroImageUrl(hero, mode);
  const hasHeroImage = !!heroImageUrl && !imageError && !isGone;

  useEffect(() => {
    setImageError(false);
  }, [heroImageUrl]);

  const title = name || "Untitled";
  const subtitle = isGone ? "Unavailable" : locationLabel;

  return (
    <BaseEntityCard
      layout="stack"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onQuickChatSubmit={onQuickChatSubmit}
      actions={actions}
      processes={processes}
      toolbarMode={toolbarMode}
      chatOpen={chatOpen}
      onChatOpenChange={onChatOpenChange}
      className={cn(
        "group relative overflow-hidden",
        embedded
          ? "w-full h-full min-h-0 rounded-xl border-0 shadow-none flex flex-col bg-card [&>div]:flex [&>div]:flex-1 [&>div]:min-h-0 [&>div]:flex-col"
          : "rounded-2xl border shadow-md",
        selected &&
          !embedded &&
          "border-rust/60 ring-2 ring-rust/60 ring-offset-2 ring-offset-background",
        className,
      )}
      style={style}
    >
      <div
        className={cn(
          "relative w-full overflow-hidden bg-surface-card",
          embedded ? "flex-1 min-h-0" : "aspect-[5/3]",
        )}
      >
        {hasHeroImage ? (
          <img
            src={heroImageUrl}
            alt={title}
            // draggable=false: a native <img> drag advertises "Files" and
            // lights up upload drop zones; the node drags via React Flow.
            draggable={false}
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Frame
              className={cn(
                "h-10 w-10",
                isGone
                  ? "text-muted-foreground/30"
                  : "text-muted-foreground/50",
              )}
              strokeWidth={1.5}
              aria-hidden
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          "flex items-center gap-2 shrink-0 border-t bg-card",
          embedded ? "px-2.5 py-2" : "px-3 py-3",
        )}
      >
        <Frame className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "truncate font-medium leading-snug",
              embedded ? "text-[12px]" : "text-[13px]",
              isGone && "text-muted-foreground",
            )}
            title={title}
          >
            {title}
          </p>
          {subtitle && (
            <p
              className={cn(
                "truncate text-muted-foreground",
                embedded ? "text-[10px]" : "text-[11px]",
              )}
              title={subtitle}
            >
              {subtitle}
            </p>
          )}
        </div>
        {onOpenClick && !isGone && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Open canvas"
            title="Open canvas"
            // nodrag: a React Flow node would otherwise arm a drag on mousedown.
            className={cn(
              "nodrag h-6 w-6 shrink-0 text-muted-foreground transition-opacity duration-150",
              "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
            )}
            onClick={(e) => {
              e.stopPropagation();
              onOpenClick();
            }}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </BaseEntityCard>
  );
}
